import { GetUserData } from "../common/service/CommonService";
import {
  LIBRARY_PATHS,
  STUDY_PATHS,
  MASTER_STUDY_PATHS,
  STUDY_LITE_PATHS,
  VOLUNTEER_PATHS,
  ALIQOUT_PATHS,
  SPONSOR_PATHS,
  MASTER_PERIOD_PATHS,
  MASTER_AE_PATHS,
  MASTER_TG_PATHS,
  IP_PATHS,
  DISPENSING_LOG_PATHS,
  LINE_CLEARANCE_PATHS,
  RETURNS_LOG_PATHS,
  DASHBOARD_PATHS,
  PRINT_LABEL_PATHS,
  ACCESSIONING_PATHS,
  RETENTION_PATHS,
  DISPOSAL_PATHS,
  TRANSFER_PATHS,
  AUDIT_PATHS,
  SCANNING_PATHS,
  REPORTS_PATHS,
  BLOCK_VOLUNTEER_PATHS,
  DROPOUT_VOLUNTEER_PATHS,
  TRANSFER_VOLUNTEER_PATHS,
  IP_STORAGE_PATHS,
} from "../Routes/paths";
import { PERMISSION_MODULES } from "../common/constants/PermissionConstants";

/* --------------------------------
 Types
--------------------------------- */

interface NavItem {
  formname: string;
  formurl?: string;
  iconclass?: string;
  formactiontags?: string;
  module?: string;
  lstchildform?: NavItem[];
  lstsubchildform?: NavItem[];  // third-level sub-children
}

interface UserData {
  formlist?: NavItem[];
  [key: string]: unknown;
}

/* --------------------------------
 Builder
--------------------------------- */

export const buildNavData = (): NavItem[] => {
  const userData = GetUserData() as UserData | null;

  const navData: NavItem[] =
    userData && Array.isArray(userData.formlist) ? [...userData.formlist] : [];

  // Append custom root items
  navData.push(
    {
      formname: "Dashboard",
      iconclass: "ri-dashboard-fill",
      formurl: DASHBOARD_PATHS.DEFAULT,
      module: PERMISSION_MODULES.DASHBOARD,
    },
    {
      formname: "Session Dashboard",
      iconclass: "ri-dashboard-fill",
      formurl: ACCESSIONING_PATHS.DASHBOARD,
      module: PERMISSION_MODULES.SESSION,
    },
    {
      formname: "Library",
      iconclass: "ri-book-open-line",
      formurl: LIBRARY_PATHS.CODE_LIST,
    },
    {
      formname: "Study",
      iconclass: "ri-database-2-line",
      formurl: MASTER_STUDY_PATHS.LIST,
    },
    {
      formname: "Volunteer",
      iconclass: "ri-user-heart-line",
      formurl: VOLUNTEER_PATHS.LIST,
      module: `${PERMISSION_MODULES.VOLUNTEER},${PERMISSION_MODULES.STUDY_SCREENING},${PERMISSION_MODULES.DROPOUT_VOLUNTEER},${PERMISSION_MODULES.BLOCKED_VOLUNTEER},${PERMISSION_MODULES.TRANSFER_VOLUNTEER}`,
    },
    {
      formname: "Clinical",
      iconclass: "ri-hospital-line",
      formurl: STUDY_PATHS.LIST,
      module: `${PERMISSION_MODULES.STUDY_DESIGN},${PERMISSION_MODULES.CLINICAL},${PERMISSION_MODULES.SUBJECTS},${PERMISSION_MODULES.SUBJECT_CHECKIN},${PERMISSION_MODULES.SUBJECT_CHECKOUT},${PERMISSION_MODULES.DOSING_SCHEDULE},${PERMISSION_MODULES.PARTICIPANTS},`,
    },
    {
      formname: "Pharmacy",
      iconclass: "ri-medicine-bottle-line",
      formurl: IP_PATHS.LIST,
    },
    {
      formname: "Labels",
      iconclass: "ri-printer-line",
      formurl: PRINT_LABEL_PATHS.TABS,
      module: `${PERMISSION_MODULES.SUBJECT_LABEL},${PERMISSION_MODULES.SUBJECT_ICARD_LABEL},${PERMISSION_MODULES.VACUTAINER_LABEL},${PERMISSION_MODULES.RIAVIAL_LABEL},${PERMISSION_MODULES.DISPENSING_LABEL}`,
    },
    {
      formname: "Session",
      iconclass: "ri-test-tube-line",
      formurl: ACCESSIONING_PATHS.LIST,
      module: PERMISSION_MODULES.SESSION,
    },
    {
      formname: "Audit Trail",
      iconclass: "ri-archive-drawer-line",
      formurl: AUDIT_PATHS.LOGS,
      module: PERMISSION_MODULES.AUDIT_TRAIL,
    },
    {
      formname: "User Activity",
      iconclass: "ri-login-box-line",
      formurl: AUDIT_PATHS.USER_ACTIVITY,
      module: PERMISSION_MODULES.AUDIT_TRAIL,
    }
  );

  // Add submenu for Library
  navData.forEach((item) => {
    if (item.formname === "Library") {
      item.lstchildform = [
        {
          formname: "Study Setup",
          lstsubchildform: [
            { formname: "Sponsor", formurl: SPONSOR_PATHS.LIST, module: PERMISSION_MODULES.SPONSOR, iconclass: "ri-building-line", },
            { formname: "Aliquot", formurl: ALIQOUT_PATHS.LIST, module: PERMISSION_MODULES.ALIQUOT, iconclass: "ri-test-tube-line", },
            { formname: "Period", formurl: MASTER_PERIOD_PATHS.LIST, module: PERMISSION_MODULES.PERIOD, iconclass: "ri-calendar-2-line", },
            { formname: "Bed Management", formurl: LIBRARY_PATHS.UNIT_LIST, module: PERMISSION_MODULES.BED_MANAGEMENT, iconclass: "ri-team-line", },
          ],
        },
        {
          formname: "User & Access",
          lstsubchildform: [
            { formname: "Role", formurl: LIBRARY_PATHS.ROLE_LIST, module: PERMISSION_MODULES.ROLE, iconclass: "ri-admin-line", },
            { formname: "User", formurl: LIBRARY_PATHS.ACCOUNTS_LIST, module: PERMISSION_MODULES.USER, iconclass: "ri-user-line", },
          ],
        },
        {
          formname: "Library Study Design",
          lstsubchildform: [
            { formname: "Workflow", formurl: LIBRARY_PATHS.WORKFLOW_LIST, module: PERMISSION_MODULES.WORKFLOW, iconclass: "ri-flow-chart", },
            { formname: "Codelist", formurl: LIBRARY_PATHS.CODE_LIST, module: PERMISSION_MODULES.CODE_LIST, iconclass: "ri-list-check", },
            { formname: "Template", formurl: LIBRARY_PATHS.TEMPLATE_LIST, module: PERMISSION_MODULES.TEMPLATE, iconclass: "ri-file-text-line", },
            { formname: "CRF", formurl: LIBRARY_PATHS.CRF_LIST, module: PERMISSION_MODULES.CRF, iconclass: "ri-file-copy-2-line", },
            { formname: "Checklist", formurl: LIBRARY_PATHS.CHECKLIST_LIST, module: PERMISSION_MODULES.CHECKLIST, iconclass: "ri-list-ordered" },
            { formname: "Design", formurl: LIBRARY_PATHS.DESIGN_LIST, module: PERMISSION_MODULES.DESIGN, iconclass: "ri-pencil-ruler-2-line", }
          ],
        },
        {
          formname: "Clinical & Safety",
          lstsubchildform: [
            { formname: "Adverse Event", formurl: MASTER_AE_PATHS.LIST, module: PERMISSION_MODULES.ADVERSE_EVENT, iconclass: "ri-alarm-warning-line", },
            { formname: "Training Group", formurl: MASTER_TG_PATHS.LIST, module: PERMISSION_MODULES.TRAINING_GROUP, iconclass: "ri-team-line", },
          ],
        },
      ];
    }
  });

  navData.forEach((item) => {
    if (item.formname === "Pharmacy") {
      item.lstchildform = [
        {
          formname: "Inventory Management",
          lstsubchildform: [
            {
              formname: "IP Inventory",
              formurl: IP_PATHS.LIST,
              iconclass: "ri-capsule-line",
              module: PERMISSION_MODULES.IP_INVENTORY,
            },
            {
              formname: "Drug Retention",
              formurl: RETENTION_PATHS.LIST,
              iconclass: "ri-archive-line",
              module: PERMISSION_MODULES.DRUG_RETENTION,
            },
            {
              formname: "IP Storage",
              formurl: IP_STORAGE_PATHS.LIST,
              iconclass: "ri-fridge-line",
              module: PERMISSION_MODULES.IP_STORAGE,
            },
          ],
        },
        {
          formname: "Dispensing Operations",
          lstsubchildform: [
            {
              formname: "Line Clearance",
              formurl: LINE_CLEARANCE_PATHS.LIST,
              iconclass: "ri-eraser-line",
              module: PERMISSION_MODULES.LINE_CLEARANCE,
            },
            {
              formname: "Dispensing Log",
              formurl: DISPENSING_LOG_PATHS.LIST,
              iconclass: "ri-medicine-bottle-line",
              module: PERMISSION_MODULES.DISPENSING_LOG,
            },
            {
              formname: "Returns Log",
              formurl: RETURNS_LOG_PATHS.LIST,
              iconclass: "ri-arrow-go-back-line",
              module: PERMISSION_MODULES.RETURNS_LOG,
            },
          ],
        },
        {
          formname: "Transfer & Disposal",
          lstsubchildform: [
            {
              formname: "Drug Transfer",
              formurl: TRANSFER_PATHS.LIST,
              iconclass: "ri-truck-line",
              module: PERMISSION_MODULES.DRUG_TRANSFER,
            },
            {
              formname: "Drug Destruction",
              formurl: DISPOSAL_PATHS.LIST,
              iconclass: "ri-delete-bin-7-line",
              module: PERMISSION_MODULES.DRUG_DISPOSAL,
            },
          ],
        },
        {
          formname: "Reconciliation",
          lstsubchildform: [
            {
              formname: "Stock Reconciliation",
              formurl: "/stock-reconciliation/list",
              iconclass: "ri-file-list-3-line",
              module: PERMISSION_MODULES.STOCK_RECONCILIATION,
            },
          ],
        },
        {
          formname: "Pharmacy Reports",
          lstsubchildform: [
            {
              formname: "Reports & Summary",
              formurl: REPORTS_PATHS.SUMMARY,
              iconclass: "ri-bar-chart-box-line",
              module: PERMISSION_MODULES.SUMMARY_REPORTS,
            },
          ],
        },
      ];
    }
  });

  // Add submenu for Master
  navData.forEach((item) => {
    if (item.formname === "Study") {
      item.lstchildform = [
        {
          formname: "Study Main",
          iconclass: "ri-flask-fill",
          formurl: MASTER_STUDY_PATHS.LIST,
          module: PERMISSION_MODULES.STUDY,
        },
        {
          formname: "Study Lite",
          iconclass: "ri-flask-line",
          formurl: STUDY_LITE_PATHS.LIST,
          module: PERMISSION_MODULES.STUDY,
        },
        {
          formname: "Document Management",
          iconclass: "ri-folder-open-line",
          formurl: MASTER_STUDY_PATHS.ATTACHMENTS,
          module: PERMISSION_MODULES.DOCUMENT_MANAGEMENT,
        },
      ];
    }
  });

  // Submenu for Volunteer
  navData.forEach((item) => {
    if (item.formname === "Volunteer") {
      item.lstchildform = [
        {
          formname: "Volunteer",
          iconclass: "ri-user-heart-line",
          formurl: VOLUNTEER_PATHS.LIST,
          module: PERMISSION_MODULES.VOLUNTEER,
        },
        {
          formname: "Blocked Volunteer",
          iconclass: "ri-user-forbid-line",
          formurl: BLOCK_VOLUNTEER_PATHS.LIST,
          module: PERMISSION_MODULES.BLOCKED_VOLUNTEER,
        },
        {
          formname: "Dropout Volunteer",
          iconclass: "ri-user-unfollow-line",
          formurl: DROPOUT_VOLUNTEER_PATHS.LIST,
          module: PERMISSION_MODULES.DROPOUT_VOLUNTEER,
        },
        {
          formname: "Transfer Volunteer",
          iconclass: "ri-user-shared-line",
          formurl: TRANSFER_VOLUNTEER_PATHS.LIST,
          module: PERMISSION_MODULES.TRANSFER_VOLUNTEER,
        },
      ];
    }
  });

  // Submenu for Session
  navData.forEach((item) => {
    if (item.formname === "Session") {
      item.lstchildform = [
        {
          formname: "Session",
          iconclass: "ri-test-tube-line",
          formurl: ACCESSIONING_PATHS.LIST,
          module: PERMISSION_MODULES.SESSION,
        },
        {
          formname: "Manual Entry",
          iconclass: "ri-edit-box-line",
          formurl: ACCESSIONING_PATHS.MANUAL_ENTRY,
          module: PERMISSION_MODULES.SESSION,
        },
      ];
    }
  });

  return navData;
};
